import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';

import classNames from 'classnames';

import { Button } from '../Button';
import { ArrowDown } from '../Icons';

import {
  ITreeTableProps,
  ITreeTableRowRenderProps,
  ITreeTableSection,
} from './tree-table.interface';
import styles from './tree-table.module.scss';

type TreeKey = string | number;

const ROOT_TREE_DEPTH = 0;
const TREE_CHILD_DEPTH_STEP = 1;
const SECTION_ITEM_DEPTH = 1;

const getInitialExpandedIds = (
  defaultExpandedIds?: Array<string | number>,
): Set<TreeKey> => new Set(defaultExpandedIds ?? []);

const TreeTable = <T,>(props: ITreeTableProps<T>): ReactElement => {
  const {
    items,
    sections,
    getId,
    getChildren,
    defaultExpandedIds,
    renderRowContent,
    getToggleAriaLabel,
    className,
  } = props;
  const resolvedSections: ITreeTableSection<T>[] = sections ?? [
    { id: 'default-tree-table-section', items: items ?? [] },
  ];

  const [expandedIds, setExpandedIds] = useState<Set<TreeKey>>(() =>
    getInitialExpandedIds(defaultExpandedIds),
  );

  useEffect(() => {
    setExpandedIds(getInitialExpandedIds(defaultExpandedIds));
  }, [items, sections, defaultExpandedIds]);

  const toggleExpanded = (id: TreeKey): void => {
    setExpandedIds(prevExpandedIds => {
      const nextExpandedIds = new Set(prevExpandedIds);

      if (nextExpandedIds.has(id)) {
        nextExpandedIds.delete(id);
      } else {
        nextExpandedIds.add(id);
      }

      return nextExpandedIds;
    });
  };

  const renderItem = (
    item: T,
    depth: number,
    options?: {
      isSectionHeader?: boolean;
      branchItems?: T[];
      renderBranchItems?: boolean;
    },
  ): ReactElement => {
    const {
      isSectionHeader = false,
      branchItems,
      renderBranchItems = true,
    } = options ?? {};
    const id = getId(item);
    const children = branchItems ?? (getChildren ? getChildren(item) ?? [] : []);
    const hasChildren = Boolean(children.length);
    const isExpanded = hasChildren ? expandedIds.has(id) : false;
    const rowRenderProps: ITreeTableRowRenderProps<T> = {
      item,
      depth,
      isSectionHeader,
      hasChildren,
      isExpanded,
      toggleExpanded: () => toggleExpanded(id),
    };

    const itemContent = (
      <>
        <div
          className={classNames(styles.row, {
            [styles['row--branch']]: hasChildren,
            [styles['row--leaf']]: !hasChildren,
            [styles['row--sectionHeader']]: isSectionHeader,
          })}
          style={{ '--tree-table-depth': depth } as CSSProperties}
        >
          <div className={styles.rowContent}>{renderRowContent(rowRenderProps)}</div>

          {hasChildren && (
            <Button
              size="small"
              mode="tertiary"
              color="dark"
              shape="circle"
              onClick={() => toggleExpanded(id)}
              aria-expanded={isExpanded}
              aria-label={getToggleAriaLabel?.(item, isExpanded) ?? 'Toggle row'}
              icon={
                <span
                  className={classNames(styles.toggleIcon, {
                    [styles.toggleIconExpanded]: isExpanded,
                  })}
                >
                  <ArrowDown size="16px" color="currentColor" />
                </span>
              }
            />
          )}
        </div>

        {renderBranchItems && hasChildren && isExpanded ? (
          <ul className={styles.children}>
            {children.map(child => renderItem(child, depth + TREE_CHILD_DEPTH_STEP))}
          </ul>
        ) : null}
      </>
    );

    if (isSectionHeader) {
      return itemContent;
    }

    return (
      <li key={String(id)} className={styles.item}>
        {itemContent}
      </li>
    );
  };

  return (
    <ul className={classNames(styles.treeTable, className)}>
      {resolvedSections.map(section => {
        const hasSectionHeader = Boolean(section.header);
        const isSectionExpanded = section.header
          ? expandedIds.has(getId(section.header))
          : true;

        return (
          <li key={String(section.id)} className={styles.section}>
            {section.header
              ? renderItem(section.header, ROOT_TREE_DEPTH, {
                  isSectionHeader: true,
                  branchItems: section.items,
                  renderBranchItems: false,
                })
              : null}

            {(!hasSectionHeader || isSectionExpanded) && section.items.length ? (
              <ul className={styles.sectionItems}>
                {section.items.map(item =>
                  renderItem(
                    item,
                    hasSectionHeader ? SECTION_ITEM_DEPTH : ROOT_TREE_DEPTH,
                  ),
                )}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

export { TreeTable };
